import { SendGridSettings } from "aderant-conflicts-environment";
import {
    CurrentUserContext,
    EmailErrors,
    EmailTypes,
    EmailWithContext,
    isEmailSearchStatus,
    isSearchedStatus,
    LoggedInUser,
    SearchStatuses,
    SearchVersion,
    SearchVersionChanges,
    SearchVersionNew,
    SearchVersionUnedited
} from "aderant-conflicts-models";
import { getUrlFriendlySubscriptionFromUserContext } from "../../OAuth/Subscription";
import { EnvironmentService } from "../../index";

function getEmailContext(type: string, id: string | number, emailTo: string): EmailErrors.EmailValidationContext {
    return {
        type: type,
        id: id,
        emailTo: emailTo
    };
}

function buildEmailAssignSearch(
    context: CurrentUserContext,
    message: string,
    searchRequest: SearchVersion | SearchVersionNew,
    emailSettings: SendGridSettings,
    openSearchUrl: string
): EmailWithContext {
    const searchNumber = !searchRequest.number ? "" : searchRequest.version < 2 ? searchRequest.number : `${searchRequest.number}.${searchRequest.version}`;
    const subjectPrefix = !searchRequest.number ? "A search request" : `Search request ${searchNumber}`;
    const emailWithTemplate: EmailWithContext["email"] = {
        from: emailSettings.fromEmailAddress,
        personalizations: [
            {
                //We put the userId into 'to' here - the enQueueEmail will lookup the email address and replace it
                to: searchRequest.assignedToUserId ?? "",
                dynamicTemplateData: {
                    subjectPrefix: subjectPrefix,
                    openSearchUrl: openSearchUrl,
                    searchNumber: searchNumber,
                    searchId: searchRequest.searchId,
                    //TODO(4855) cast can go away when this WI is completed - need to either just stick user name in the stuff that always goes to KeyAuth/QueueTrigger functions, or make it an input param here and acquire it outside of this code.
                    //justification: code will run fine if context.currentUser isn't a LoggedInUser, will just grab the id instead.
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    assignedBy: (context.currentUser as LoggedInUser).name ?? context.currentUser.id,
                    message: message
                }
            }
        ],
        customArgs: {
            tenancy: context.currentUser.tenancy,
            sourceApp: "Conflicts",
            emailType: EmailTypes.ASSIGN_SEARCH
        },
        templateId: emailSettings.assignSearchTemplateId
    };
    const emailWithContext: EmailWithContext = {
        email: emailWithTemplate,
        context: getEmailContext("searchRequest", searchRequest.id, searchRequest.assignedToUserId ?? "")
    };
    return emailWithContext;
}

function buildEmailCompleteSearch(
    context: CurrentUserContext,
    message: string,
    searchRequest: SearchVersion | SearchVersionNew,
    emailSettings: SendGridSettings,
    openSearchUrl: string
): EmailWithContext {
    const searchNumber = !searchRequest.number ? "" : searchRequest.version < 2 ? searchRequest.number : `${searchRequest.number}.${searchRequest.version}`;
    const subjectPrefix = !searchRequest.number ? "A search request" : `Search request ${searchNumber}`;
    const emailWithTemplate: EmailWithContext["email"] = {
        from: emailSettings.fromEmailAddress,
        personalizations: [
            {
                //We put the userId into 'to' here - the enQueueEmail will lookup the email address and replace it
                to: searchRequest.requestedByUserId ?? "",
                dynamicTemplateData: {
                    subjectPrefix: subjectPrefix,
                    openSearchUrl: openSearchUrl,
                    searchNumber: searchNumber,
                    searchId: searchRequest.searchId,
                    status: searchRequest.status == SearchStatuses.ConditionalApproval ? "conditionally approved" : searchRequest.status.toLocaleLowerCase(),
                    //TODO(4855) cast can go away when this WI is completed - need to either just stick user name in the stuff that always goes to KeyAuth/QueueTrigger functions, or make it an input param here and acquire it outside of this code.
                    //justification: code will run fine if context.currentUser isn't a LoggedInUser, will just grab the id instead.
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    completedBy: (context.currentUser as LoggedInUser).name ?? context.currentUser.id,
                    message: message
                }
            }
        ],
        customArgs: {
            tenancy: context.currentUser.tenancy,
            sourceApp: "Conflicts",
            emailType: EmailTypes.COMPLETE_SEARCH
        },
        templateId: emailSettings.completeSearchTemplateId
    };
    const emailWithContext: EmailWithContext = {
        email: emailWithTemplate,
        context: getEmailContext("searchRequest", searchRequest.id, searchRequest.requestedByUserId ?? "")
    };
    return emailWithContext;
}

function buildEmailShareWithReviewers(
    context: CurrentUserContext,
    searchRequest: SearchVersion | SearchVersionNew,
    emailSettings: SendGridSettings,
    openSearchUrl: string,
    sharedToUserEmail: string
): EmailWithContext {
    const emailWithTemplate: EmailWithContext["email"] = {
        from: emailSettings.fromEmailAddress,
        personalizations: [
            {
                to: sharedToUserEmail ?? "",
                dynamicTemplateData: {
                    openSearchUrl: openSearchUrl,
                    subject: "Please Review Potential Conflicts of Interest.",
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    sharedBy: (context.currentUser as LoggedInUser).name ?? context.currentUser.id
                }
            }
        ],
        customArgs: {
            tenancy: context.currentUser.tenancy,
            sourceApp: "Conflicts",
            emailType: EmailTypes.HITS_SHARED
        },
        templateId: emailSettings.reviewConflictsEmailTemplateId
    };

    const emailWithContext: EmailWithContext = {
        email: emailWithTemplate,
        context: getEmailContext("searchRequest", searchRequest.id, sharedToUserEmail ?? "")
    };
    return emailWithContext;
}

export function buildEmails(
    context: CurrentUserContext,
    reassignMessage?: string,
    statusNotificationMessage?: string,
    searchRequest?: SearchVersion | SearchVersionNew | SearchVersionUnedited,
    searchChanges?: SearchVersionChanges,
    sharedToUserEmail?: string
): EmailWithContext[] {
    const settings = EnvironmentService.getSendGridSettings();
    if (!settings) {
        context.logger.error("No SendGrid settings found in configuration. No emails will be sent.");
        return [];
    }
    if (!settings.fromEmailAddress) {
        context.logger.error("No SendGrid settings for from email address found in configuration. No emails will be sent.");
        return [];
    }
    const webAppUrl = EnvironmentService.getClientWebAppUrl();
    const subscriptionUrlParam = `?subscription=${getUrlFriendlySubscriptionFromUserContext(context)}`;
    let openSearchUrl = "";
    if (searchRequest && isSearchedStatus(searchRequest.status)) {
        openSearchUrl = `${webAppUrl}/results/${searchRequest.searchId}${subscriptionUrlParam}?login_hint=${searchRequest.assignedToUserId}`;
    } else {
        openSearchUrl = searchRequest ? `${webAppUrl}/search/${searchRequest.searchId}${subscriptionUrlParam}?login_hint=${searchRequest.assignedToUserId}` : "";
    }
    const emails: EmailWithContext[] = [];
    if (searchRequest && searchChanges && searchChanges.assignedToUserId && searchRequest.assignedToUserId) {
        if (settings.assignSearchTemplateId) {
            const email = buildEmailAssignSearch(context, reassignMessage ?? "", searchRequest, settings, openSearchUrl);
            emails.push(email);
        } else {
            context.logger.error("No SendGrid setting for assigned search template id found in configuration. Assign search email not sent.");
        }
    }
    if (searchRequest && searchChanges && searchChanges.status && isEmailSearchStatus(searchRequest.status)) {
        if (settings.completeSearchTemplateId) {
            const email = buildEmailCompleteSearch(context, statusNotificationMessage ?? "", searchRequest, settings, openSearchUrl);
            emails.push(email);
        } else {
            context.logger.error("No SendGrid setting for complete search template id found in configuration. Complete search email not sent.");
        }
    }

    if (searchRequest && sharedToUserEmail) {
        openSearchUrl = `${webAppUrl}/results/${searchRequest.searchId}${subscriptionUrlParam}?login_hint=${sharedToUserEmail}`;
        if (settings.reviewConflictsEmailTemplateId) {
            // FIXME: there should be a value for reviewConflictsEmailTemplateId in the environmentConfig
            const email = buildEmailShareWithReviewers(context, searchRequest, settings, openSearchUrl, sharedToUserEmail);
            emails.push(email);
        } else {
            context.logger.error("No SendGrid setting for review conflicts template id found in configuration. Reviewer email not sent.");
        }
    }
    return emails;
}
